import React, { useEffect, useState } from 'react';
import { Blocks, Settings2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { EXTENSIONS } from '@/extensions/registry';
import { useExtensionsStore } from '@/stores/extensionsStore';
import { ExtensionsSettings } from './ExtensionsSettings';
import { DockGlyph } from '@/components/toolbar/DockGlyph';

/** Toolbar entry point: lists enabled extensions and opens their panels. */
export function ExtensionsLauncher() {
  const { loaded, load, isEnabled } = useExtensionsStore();
  // Subscribe to rows so the list re-renders when a switch flips.
  useExtensionsStore((s) => s.rows);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activePanel, setActivePanel] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    if (!loaded) load();
  }, [loaded, load]);

  const enabled = EXTENSIONS.filter((e) => isEnabled(e.manifest.id));

  return (
    <>
      <Popover open={menuOpen} onOpenChange={setMenuOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-10 w-10 rounded-full hover:bg-muted/60 text-foreground"
            aria-label="Extensions"
            title="Extensions"
            data-dock-item
          >
            <DockGlyph>
              <Blocks className="h-5 w-5" />
            </DockGlyph>
          </Button>
        </PopoverTrigger>
        <PopoverContent side="top" align="end" sideOffset={12} className="w-64 p-1.5 rounded-xl">
          <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">Extensions</div>
          {enabled.length === 0 ? (
            <p className="px-2 pb-2 text-xs text-muted-foreground">
              No extensions turned on yet.
            </p>
          ) : (
            enabled.map(({ manifest, Icon }) => (
              <button
                key={manifest.id}
                type="button"
                className="w-full flex items-center gap-2.5 rounded-lg px-2 py-2 text-left hover:bg-muted/60 transition-colors"
                onClick={() => {
                  setMenuOpen(false);
                  setActivePanel(manifest.id);
                }}
              >
                <Icon className="h-4 w-4 shrink-0" style={{ color: manifest.accent }} />
                <span className="text-sm">{manifest.name}</span>
              </button>
            ))
          )}
          <div className="border-t border-border/50 mt-1 pt-1">
            <button
              type="button"
              className="w-full flex items-center gap-2.5 rounded-lg px-2 py-2 text-left hover:bg-muted/60 transition-colors text-muted-foreground"
              onClick={() => {
                setMenuOpen(false);
                setSettingsOpen(true);
              }}
            >
              <Settings2 className="h-4 w-4 shrink-0" />
              <span className="text-sm">Manage extensions</span>
            </button>
          </div>
        </PopoverContent>
      </Popover>

      {EXTENSIONS.map(({ manifest, Panel }) => (
        <Panel
          key={manifest.id}
          open={activePanel === manifest.id}
          onOpenChange={(open) => setActivePanel(open ? manifest.id : null)}
        />
      ))}

      <ExtensionsSettings open={settingsOpen} onOpenChange={setSettingsOpen} />
    </>
  );
}
